import { useContext } from 'react'

import { F0Button, F0TagStatus, StatusVariant } from '@factorialco/f0-react'
import { ExternalLink, ArrowCycle } from '@factorialco/f0-react/icons/app'
import { Box, List, Text } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'

import i18n from 'modules/core/lib/i18n'
import { FinanceSettingsSalesPageDocument, RegisterSupplierTaxBridgeDatumDocument, TaxBridgeDataFragment } from 'generated/resources/graphql'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

const getActionIcon = (registrationState: string) => {
  if (registrationState === 'error') {
    return ArrowCycle
  }

  return ExternalLink
}

const getRegisteringMessage = (registrationState: string, linkUrl?: string | null) => {
  if (registrationState === 'processing') {
    return linkUrl ? i18n.t('finance.sale_invoices.settings.verifactu.signed_agreement')
    : i18n.t('finance.sale_invoices.settings.verifactu.waiting_response')
  }
  return null
}

const getStatusVariant = (state: string): StatusVariant => {
  if (state === 'error') return 'critical'
  if (state === 'processing') return 'warning'
  if (state === 'registered') return 'positive'
  return 'neutral'
}

const getStatusName = (state: string): string => {
  if (state === 'error') return i18n.t('finance.sale_invoices.settings.verifactu.status.error')
  if (state === 'processing') return i18n.t('finance.sale_invoices.settings.verifactu.status.processing')
  if (state === 'registered') return i18n.t('finance.sale_invoices.settings.verifactu.status.registered')
  return i18n.t('finance.sale_invoices.settings.verifactu.status.pending')
}

interface Props {
  legalEntityId: number
  legalEntityName: string
  taxBridgeData?: TaxBridgeDataFragment
  onUpdate: () => void
}

export const ListItemVerifactu = ({
  legalEntityId,
  legalEntityName,
  taxBridgeData,
  onUpdate,
}: Props) => {
  // ignoreUpsell is true in demo environment
  const { ignoreUpsell } = useContext(EnvironmentContext)

  const [registerSupplier] = useMutation(RegisterSupplierTaxBridgeDatumDocument, {
    extractorFn: (dataParam) => dataParam?.financeMutations.registerSupplierFinanceTaxBridgeDatum,
    refetchQueries: [FinanceSettingsSalesPageDocument],
  })

  const handleRegisterSupplier = async () => {
    const handledRegisterSupplier = handleResult(
      await registerSupplier({
        variables: {
          legalEntityId,
        },
      }),
      (_errors) => {
        handleResultWithFlash()
      }
    )

    if (handledRegisterSupplier.success) {
      onUpdate()
    }
  }

  const registrationState = !taxBridgeData ? 'pending' : taxBridgeData?.state || 'processing'
  const linkUrl = taxBridgeData?.linkUrl

  const handleOnClickAction = () => {
    if (registrationState === 'error' || registrationState === 'pending') {
      handleRegisterSupplier()
      return
    }

    if (linkUrl) {
      window.open(linkUrl, '_blank')
    }
  }

  const registeringMessage = getRegisteringMessage(registrationState, linkUrl)

  const loading = registrationState == 'processing' && !linkUrl

  const disabled = loading || ignoreUpsell

  return (
    <List.Item>
      <Box width="full" flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s8">
        <Box flexDirection="column" gap="s4">
        <Text.Normal weight="medium">{legalEntityName}</Text.Normal>
        {registeringMessage && <Text.Normal size="200" weight="normal" color="tangerine1000">{registeringMessage}</Text.Normal>}
        {taxBridgeData?.jobErrors && <Text.Normal size="200" weight="normal" color="radicalRed1000">{JSON.parse(taxBridgeData.jobErrors).join(', ')}</Text.Normal>}
        </Box>
        <Box flexDirection="row" alignItems="center" gap="s12">
          <F0TagStatus text={getStatusName(registrationState)} variant={getStatusVariant(registrationState)} />
          <F0Button loading={loading} disabled={disabled} variant="outline" onClick={handleOnClickAction} hideLabel icon={getActionIcon(registrationState)}/>
        </Box>
      </Box>
    </List.Item>
  )
}
